import * as React from "react";
import * as apiAccounts from "../../../api/accounts";
import { Button, ButtonSize, ButtonType } from "../../../components/button";
import { InputSize } from "../../../components/input";
import { Loader, LoaderStyle } from "../../../components/loader";
import { CheckColumn, ITableColumn, ITableRowEventArgs, Table, TextWithIconColumn } from "../../../components/table";
import { TextBox } from "../../../components/textBox";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../../../components/toolbar";
import { IUser } from "../../../models/get";
import { ISubscription } from "../../../models/get/subscription";
import SettingsNav from "../nav";
import Dialog from ".//dialog";

interface IState {
    currentUser?: IUser;
    isLoading?: boolean,
    isDialogOpen?: boolean,
    isDialogInEditMode?: boolean,
    filterValue?: string,
    subscriptions?: ISubscription[],
    filteredSubscriptions?: ISubscription[],
    selectedItem?: ISubscription,
    selectedRowIndex?: string
}

export class UserProfileNotifications extends React.Component<{}, IState>
{
    constructor(props: any)
    {
        super(props);

        this.state = {
            isLoading: true
        }

        this.initAsync();
    }

    private async initAsync()
    {
        const currentUser = await apiAccounts.getCurrentUser();
        const subscriptions = await apiAccounts.getCurrentUsersSubscriptions();

        this.setState({
            isLoading: false,
            currentUser: currentUser,
            subscriptions: subscriptions,
            filteredSubscriptions: subscriptions
        });
    }

    private async refreshAsync() {
        this.setState({
            isLoading: true,
            filterValue: null
        });

        await this.initAsync();
    }

    private onFilterTextChanged = (value: any[], originalValue: string[]): void => {
        const { subscriptions } = this.state;
        var filter: string = value[0];
        var filteredData: ISubscription[] = [];

        if (filter != null && filter !== "")
            filteredData = subscriptions.where(n => n.description.toLowerCase().indexOf(filter.toLowerCase()) >= 0);
        else
            filteredData = subscriptions;

        this.setState({
            filterValue: value[0],
            filteredSubscriptions: filteredData
        });
    }

    private onRowClick = (e: ITableRowEventArgs<any>) => {
        this.setState({
            selectedItem: e.dataSource,
            selectedRowIndex: e.index
        });
    }

    private onNewClick = () => {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: false
        });
    }

    private onEditClick = () => {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: true
        });
    }

    private onDialogClosed = async (refresh: boolean) => {
        if (refresh) {
            await this.refreshAsync();
            this.setState({
                isDialogOpen: false,
                selectedItem: null,
                selectedRowIndex: null
            });
        }
        else {
            this.setState({
                isDialogOpen: false
            });
        }
    }

    private renderInfoText(): JSX.Element {
        return (
            <div className="settings-default_empty">
                <i className="settings-default_empty_icon mdi mdi-comment-multiple"></i>
                <div className="settings-default_empty_header">Benachrichtigungen</div>
                <div
                    style={{
                        width: 370
                    }}
                    className="settings-default_empty_text">
                    Erstellen Sie neue Regeln für Benachrichtigungen.
                </div>
                <Button
                    icon="plus"
                    text="Benachrichtigung (Regel)"
                    type={ButtonType.primary}
                    size={ButtonSize.big}
                    onClick={this.onNewClick} />
            </div>
        );
    }

    private renderTable(): JSX.Element {
        const { currentUser, subscriptions, filteredSubscriptions } = this.state;
        const columns: ITableColumn[] = [
            {
                header: "Beschreibung",
                width: 400,
                cell: (data: ISubscription) => {
                    return <TextWithIconColumn text={data.description} icon="earth" iconAlignRight={true} iconTitle="Globale Regel" width={400} />
                },
            },
            {
                header: "Typ",
                width: 200,
                computedValue: (data: ISubscription) =>
                {
                    const typ: string[] = [];
                    if (data.action_created)
                        typ.push("erstellen");

                    if (data.action_changed)
                        typ.push("bearbeiten");

                    if (data.action_deleted)
                        typ.push("löschen");

                    return typ.join(", ");
                }
            },
            {
                header: "Benachrichtigt",
                width: 300,
                cell: (data: ISubscription) =>
                {
                    let text: string = "";
                    const isUserSubscriber: boolean = data.subscribers.length === 1 && data.subscribers.any(s => s.subscriber_type === "User");
                    if (isUserSubscriber)
                        text = data.subscribers.select(s => s.subscriber_name).join(", ");
                    else
                        text = data.subscribers.where(s => s.subscriber_type !== "User").select(s => s.subscriber_name).join(", ");

                    return <TextWithIconColumn text={text} profile={isUserSubscriber && currentUser.avatar} width={300} />
                },
            },
            {
                header: "Status",
                width: 100,
                cell: (data: ISubscription) => {
                    const sub = data.subscribers.where(s => (s.subscriber_type === "User" && s.subscriber === currentUser.id) || (s.subscriber_type === "Group" && s.subscriber === currentUser.group_id));
                    return <CheckColumn isChecked={(sub.length > 0 ? !sub.any(s => s.canceled) : false)} width={100} />;
                },
            }
        ];

        if (subscriptions != null && filteredSubscriptions.length > 0) {
            return (
                <Table
                    style={{
                        position: "relative"
                    }}
                    dataSource={filteredSubscriptions}
                    columns={columns}
                    keyAccessor="id"
                    onRowClick={this.onRowClick} />
            );
        }
        else {
            return this.renderInfoText();
        }
    }

    public render(): JSX.Element {
        const { isLoading, isDialogOpen, isDialogInEditMode, filterValue, subscriptions, selectedItem } = this.state;
        const isListEmpty: boolean = subscriptions == null || subscriptions.length === 0;
        const isItemSelected: boolean = selectedItem != null;

        if (isLoading) {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1}
                    infoText="Einstellungen für Benachrichtigungen werden geladen" />
            );
        }

        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="notifications" />
                    </div>
                    <div className="settings-default-container">
                        {
                            !isListEmpty &&
                            <div className="settings-default_toolbar">
                                <ToolBar hasTopBorder={false}>
                                    <ToolBarItem
                                        icon="plus"
                                        text="Neu"
                                        onClick={this.onNewClick} />
                                    <ToolBarSeparator />
                                    <ToolBarItem
                                        icon="pencil"
                                        text="Bearbeiten"
                                        onClick={this.onEditClick}
                                        isDisabled={!isItemSelected} />
                                </ToolBar>
                            </div>
                        }
                        {
                            !isListEmpty &&
                            <div className="settings-default_filter">
                                <TextBox
                                    placeholder="Filter Name..."
                                    size={InputSize.normal}
                                    value={filterValue}
                                    onTextChange={this.onFilterTextChanged} />

                            </div>
                        }
                        <div className="settings-default">
                            {this.renderTable()}
                        </div>
                        {
                            isDialogOpen &&
                            <Dialog
                                subscriptionId={isDialogInEditMode && isItemSelected && selectedItem.id}
                                onClose={this.onDialogClosed} />
                        }
                    </div>
                </div>
            </div>
        );
    }
}